import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Vertical image marquee with opposite-scrolling columns.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} source
 * @property {HTMLElement[]} tracks
 *
 * @extends {Component<Refs>}
 */
class VerticalImageScroll extends Component {
  static get observedAttributes() {
    return ['data-columns'];
  }

  /** @type {MediaQueryList} */
  #desktopMedia = window.matchMedia('(min-width: 750px)');

  /** @type {MediaQueryList} */
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {MutationObserver | null} */
  #sourceObserver = null;

  /** @type {MutationObserver | null} */
  #hostObserver = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {IntersectionObserver | null} */
  #imageObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  #building = false;
  #rebuildScheduled = false;
  #needsRebuild = false;
  #widthRetries = 0;
  #inView = true;
  #visibilityKnown = false;

  get #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#desktopMedia.addEventListener('change', this.#onMediaChange);

    if (this.#isDesignMode) {
      this.#observe();
      this.#observeResize();
      this.#bindEditor();
      this.#scheduleBuild();
      return;
    }

    this.#observeVisibility();
    this.#observeResize();
    this.#scheduleBuild();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#desktopMedia.removeEventListener('change', this.#onMediaChange);
    this.#onMediaChange.cancel();
    this.#sourceObserver?.disconnect();
    this.#hostObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    this.#imageObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#unbindEditor();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#isDesignMode) {
      this.#observe();
      this.#scheduleBuild();
    }
  }

  /**
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    this.#scheduleBuild();
  }

  #onMediaChange = debounce(() => {
    if (!this.isConnected) return;
    this.#scheduleBuild();
  }, 150);

  #scheduleBuild() {
    if (this.#building) {
      this.#needsRebuild = true;
      return;
    }
    if (this.#rebuildScheduled) return;
    this.#rebuildScheduled = true;
    requestAnimationFrame(() => {
      this.#rebuildScheduled = false;
      this.#build();
    });
  }

  #observeVisibility() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        const inView = Boolean(entries[0]?.isIntersecting);
        this.#inView = inView;
        this.#visibilityKnown = true;
        this.classList.toggle('vis--offscreen', !inView);

        if (inView && !this.classList.contains('vis--ready')) {
          this.#scheduleBuild();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#visibilityObserver.observe(this);
  }

  #observeResize() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.clientWidth < 2) return;
      this.#widthRetries = 0;
      this.#syncImageSizes();
      if (!this.classList.contains('vis--ready')) {
        this.#scheduleBuild();
      }
    });
    this.#resizeObserver.observe(this);
  }

  #observe() {
    this.#hostObserver?.disconnect();
    this.#hostObserver = new MutationObserver(this.#onHostMutate);
    this.#hostObserver.observe(this, {
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-columns'],
    });

    const source = this.#getSource();
    if (source) {
      this.#sourceObserver?.disconnect();
      this.#sourceObserver = new MutationObserver(this.#onSourceMutate);
      this.#sourceObserver.observe(source, { childList: true, subtree: true });
    }
  }

  /**
   * @param {Node} node
   * @returns {boolean}
   */
  #isLayoutNode(node) {
    return (
      node instanceof Element &&
      (node.matches('.vis__source, .vis__viewport, .vis__column, .vis__track, .vis__item') ||
        Boolean(node.querySelector('.vis__source, .vis__viewport, .vis__column, .vis__track, .vis__item')))
    );
  }

  /**
   * @param {MutationRecord[]} mutations
   */
  #onHostMutate = (mutations) => {
    if (this.#building) return;

    const structureChanged = mutations.some((mutation) => {
      if (mutation.type !== 'childList') return false;
      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.#isLayoutNode(node));
    });
    const shouldRebuild = mutations.some((mutation) => {
      if (mutation.type === 'childList') {
        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.#isLayoutNode(node));
      }
      if (mutation.attributeName === 'data-columns' || mutation.attributeName === 'style') return true;
      if (mutation.attributeName === 'class') {
        return !this.classList.contains('vis--ready') && this.#getItems().length > 0;
      }
      return false;
    });

    if (structureChanged) this.#observe();
    if (shouldRebuild) this.#scheduleBuild();
  };

  #onSourceMutate = () => {
    if (this.#building) return;
    this.#scheduleBuild();
  };

  #bindEditor() {
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  #unbindEditor() {
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /**
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('.vis__item') && this.contains(target)) {
      this.classList.add('vis--inspect');
    }
  };

  #onBlockDeselect = () => {
    this.classList.remove('vis--inspect');
  };

  /**
   * @param {Event} event
   */
  #onSectionLoad = (event) => {
    const section = event.target;
    if (!(section instanceof HTMLElement) || !section.contains(this)) return;
    this.#observe();
    this.#scheduleBuild();
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getSource() {
    const source = this.querySelector('.vis__source');
    return source instanceof HTMLElement ? source : null;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getTracks() {
    return [...this.querySelectorAll('.vis__column > .vis__track')].filter(
      (track) => track instanceof HTMLElement
    );
  }

  /**
   * @returns {Element[]}
   */
  #getItems() {
    const source = this.#getSource();
    if (!source) return [];
    return [...source.querySelectorAll(':scope > .vis__item')];
  }

  /**
   * @returns {number}
   */
  #getColumnCount() {
    const desktopColumns = Number(this.dataset.columns) || 2;
    if (this.#desktopMedia.matches) return desktopColumns;
    return 2;
  }

  /**
   * @param {string} raw
   * @returns {number}
   */
  #parseRatio(raw) {
    const value = (raw || '3 / 4').trim() || '3 / 4';
    const parts = value.split('/').map((part) => Number.parseFloat(part.trim()));
    if (parts.length === 2 && parts[0] && parts[1]) return parts[0] / parts[1];
    const numeric = Number.parseFloat(value);
    return numeric > 0 ? numeric : 0.75;
  }

  /**
   * @param {number} itemCount
   * @param {{ columnWidth: number, gap: number, ratio: number }} metrics
   * @returns {number}
   */
  #estimateSetHeight(itemCount, metrics) {
    const itemHeight = Math.max(metrics.columnWidth, 1) / metrics.ratio;
    return itemCount * (itemHeight + metrics.gap);
  }

  /**
   * Point srcset at the rendered column width so the browser fetches one matching size.
   * @param {number} [widthHint]
   */
  #syncImageSizes(widthHint) {
    let width = widthHint ?? 0;
    if (!(width > 1)) {
      const column = this.querySelector('.vis__column');
      if (!(column instanceof HTMLElement)) return;
      width = Math.round(column.getBoundingClientRect().width);
    }
    if (width < 2) return;

    const sizes = `${Math.round(width)}px`;
    this.querySelectorAll('img.vis__image').forEach((img) => {
      if (img instanceof HTMLImageElement) {
        img.sizes = sizes;
      }
    });
  }

  /**
   * Load lazy images once they are near the visible area (page scroll or column animation).
   */
  #observeImages() {
    if (!('IntersectionObserver' in window)) return;

    const images = [...this.querySelectorAll('img.vis__image[loading="lazy"]')];
    this.#imageObserver?.disconnect();
    if (images.length === 0) return;

    this.#imageObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const image = entry.target;
          if (image instanceof HTMLImageElement) {
            image.loading = 'eager';
          }
          this.#imageObserver?.unobserve(image);
        }
      },
      {
        root: null,
        rootMargin: '200px 0px',
        threshold: 0.01,
      }
    );

    for (const image of images) {
      this.#imageObserver.observe(image);
    }
  }

  /**
   * @param {HTMLElement} clone
   */
  #markClone(clone) {
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('a').forEach((anchor) => {
      if (anchor instanceof HTMLElement) {
        anchor.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * @param {Element} item
   * @returns {HTMLElement}
   */
  #cloneItem(item) {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
    this.#markClone(clone);
    return clone;
  }

  /**
   * @param {HTMLElement} track
   * @param {Element[]} items
   * @param {number} minHeight
   * @param {{ columnWidth: number, gap: number, ratio: number }} metrics
   */
  #fillTrack(track, items, minHeight, metrics) {
    if (items.length === 0) return;

    const setHeight = this.#estimateSetHeight(items.length, metrics);
    const repeats = Math.min(4, Math.max(1, Math.ceil((minHeight * 1.2) / Math.max(setHeight, 1))));
    const fragment = document.createDocumentFragment();
    /** @type {Element[]} */
    const firstHalf = [];

    for (let repeat = 0; repeat < repeats; repeat++) {
      items.forEach((item) => {
        const node = repeat === 0 ? item : this.#cloneItem(item);
        fragment.appendChild(node);
        firstHalf.push(node);
      });
    }

    firstHalf.forEach((item) => {
      fragment.appendChild(this.#cloneItem(item));
    });

    track.appendChild(fragment);
  }

  #restartAnimation() {
    this.classList.add('vis--restart');
    requestAnimationFrame(() => {
      this.classList.remove('vis--restart');
    });
  }

  #build() {
    if (this.#building) return;
    if (this.#reduceMotion.matches) {
      this.classList.remove('vis--ready');
      this.#syncImageSizes();
      this.#observeImages();
      return;
    }

    const source = this.#getSource();
    const tracks = this.#getTracks();
    if (!source || tracks.length === 0) return;

    const items = this.#getItems();
    if (items.length === 0) {
      this.classList.remove('vis--ready');
      return;
    }

    const hostWidth = this.clientWidth;
    if (hostWidth < 2) {
      if (this.#widthRetries < 12) {
        this.#widthRetries += 1;
        requestAnimationFrame(() => this.#scheduleBuild());
      }
      return;
    }

    const styles = getComputedStyle(this);
    const gap = Number.parseFloat(styles.getPropertyValue('--vis-gap')) || 16;
    const stagger = Number.parseFloat(styles.getPropertyValue('--vis-stagger')) || 0;
    const ratio = this.#parseRatio(styles.getPropertyValue('--vis-ratio'));
    const columnCount = this.#getColumnCount();
    const viewport = this.querySelector('.vis__viewport');
    const viewportHeight = viewport instanceof HTMLElement ? viewport.clientHeight : this.clientHeight;
    const availableWidth = Math.max(hostWidth - 32, 1);
    const columnWidth = (availableWidth - gap * (columnCount - 1)) / Math.max(columnCount, 1);
    const metrics = { columnWidth, gap, ratio };

    this.#widthRetries = 0;
    this.#building = true;
    this.#syncImageSizes(Math.round(columnWidth));

    /** @type {HTMLElement[][]} */
    const columns = Array.from({ length: columnCount }, () => []);

    items.forEach((item, index) => {
      columns[index % columnCount]?.push(this.#cloneItem(item));
    });

    tracks.forEach((track, index) => {
      track.replaceChildren();

      if (index >= columnCount) return;

      let colItems = columns[index] ?? [];
      if (colItems.length === 0) {
        colItems = items.map((item) => this.#cloneItem(item));
      }

      const extra = track.closest('.vis__column--up') ? stagger : 0;
      this.#fillTrack(track, colItems, viewportHeight + extra, metrics);
    });

    this.classList.add('vis--ready');
    this.classList.toggle('vis--offscreen', !this.#isDesignMode && this.#visibilityKnown && !this.#inView);
    this.#syncImageSizes(Math.round(columnWidth));
    this.#observeImages();
    this.#restartAnimation();
    this.#hostObserver?.takeRecords();
    this.#sourceObserver?.takeRecords();
    this.#building = false;

    if (this.#needsRebuild) {
      this.#needsRebuild = false;
      this.#scheduleBuild();
    }
  }
}

if (!customElements.get('vertical-image-scroll')) {
  customElements.define('vertical-image-scroll', VerticalImageScroll);
}
